import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  Menu,
  X,
  ChevronDown,
  Bus,
  Truck,
  GraduationCap,
  Warehouse,
  Phone,
  ArrowRight,
  CircleDollarSign,
  Building2,
  Users,
  BarChart3,
  Shield,
  Briefcase,
  FileCheck,
} from 'lucide-react';
import Logo from './Logo';

type MegaMenuKey = 'products' | 'solutions' | 'company';

const Header: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false);
  const [activeMegaMenu, setActiveMegaMenu] = useState<MegaMenuKey | null>(null);
  const [isScrolled, setIsScrolled] = useState<boolean>(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 10);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMenuOpen(false);
    setActiveMegaMenu(null);
  }, [location.pathname, location.search, location.hash]);

  const products = [
    {
      name: 'Employee Transport',
      icon: Bus,
      href: '/employee-transport',
      description: 'Corporate commute management for HR, admin, and transport teams.',
      meta: 'Enterprise commute',
    },
    {
      name: '3PL — Freight Operations',
      icon: Truck,
      href: '/trucks',
      description: 'Dispatch, billing, compliance, and freight control for 3PL transport businesses.',
      meta: 'Third-party logistics',
    },
    {
      name: 'SCMT — School / College',
      icon: GraduationCap,
      href: '/scmt',
      description: 'Safer student transport with parent visibility and school operations control.',
      meta: 'School & college mobility',
    },
    {
      name: 'Own Fleet Operations',
      icon: Warehouse,
      href: '/trucks',
      description: 'Manage your captive fleet end-to-end — trips, drivers, fuel, maintenance, and cost control.',
      meta: 'Captive fleet management',
    },
  ];

  const directLinks = [
    { name: 'Pricing', href: '/pricing' },
  ];

  const solutions = [
    {
      name: 'Fleet Operators',
      icon: Truck,
      href: '/trucks',
      description: 'Run dispatch, pricing, billing, and compliance from one operating layer.',
    },
    {
      name: 'Enterprise Mobility',
      icon: Users,
      href: '/employee-transport',
      description: 'Manage employee transport, shift movement, and vendor service quality.',
    },
    {
      name: 'School Operators',
      icon: Shield,
      href: '/scmt',
      description: 'Improve student safety, route visibility, and school transport coordination.',
    },
    {
      name: 'Growing Fleets',
      icon: CircleDollarSign,
      href: '/pricing',
      description: 'See which commercial model fits your fleet size, workflows, and rollout stage.',
    },
  ];

  const companyLinks = [
    {
      name: 'About Us',
      icon: Building2,
      href: '/about',
      description: 'Learn how GaadiMatic is building fleet software for Indian operations.',
    },
    {
      name: 'Leadership',
      icon: Briefcase,
      href: '/leadership',
      description: 'Meet the team shaping product, operations, and company direction.',
    },
    {
      name: 'Investors',
      icon: BarChart3,
      href: '/investors',
      description: 'Explore market context, business model, and long-term company opportunity.',
    },
    {
      name: 'Contact',
      icon: FileCheck,
      href: '/contact',
      description: 'Book a demo, request ROI analysis, or speak with the commercial team.',
    },
  ];

  const handleNavigation = (href: string) => {
    const isHashLink = href.startsWith('/#') || href.startsWith('#');

    if (isHashLink) {
      if (location.pathname !== '/') {
        navigate(href);
      } else {
        const selector = href.replace('/', '');
        const element = document.querySelector(selector);
        if (element) element.scrollIntoView({ behavior: 'smooth' });
      }
    } else {
      navigate(href);
    }
    setIsMenuOpen(false);
    setActiveMegaMenu(null);
  };

  const isProductsActive = products.some((product) => location.pathname === product.href);
  const isCompanyActive = companyLinks.some((link) => location.pathname === link.href);

  const menuButtonClass = (isActive: boolean) =>
    `flex items-center gap-1 px-3 py-2 rounded-xl font-medium transition-colors ${
      isActive ? 'bg-primary-50 text-primary-600' : 'text-gray-700 hover:bg-gray-50 hover:text-primary-600'
    }`;

  const renderMegaMenu = () => {
    if (activeMegaMenu === 'products') {
      return (
        <div className="grid lg:grid-cols-[1.35fr_0.85fr] gap-10">
          <div>
            <div className="mb-6">
              <p className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-2">Products</p>
              <h3 className="text-2xl font-bold text-navy-950">Explore GaadiMatic by operating model</h3>
            </div>

            <div className="grid grid-cols-2 gap-4">
              {products.map((product) => (
                <button
                  key={product.name}
                  onClick={() => handleNavigation(product.href)}
                  className="text-left p-4 rounded-2xl border border-gray-100 bg-white hover:border-primary-200 hover:shadow-lg transition-all"
                >
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-xl bg-primary-50 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <product.icon className="w-5 h-5 text-primary-600" />
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-primary-600 mb-1">{product.meta}</p>
                      <h4 className="text-base font-bold text-navy-950 mb-1">{product.name}</h4>
                      <p className="text-sm text-gray-500 leading-relaxed">{product.description}</p>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>

          <div className="bg-gray-50 rounded-3xl border border-gray-100 p-6">
            <p className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-2">One Platform</p>
            <h4 className="text-2xl font-bold text-navy-950 mb-4">One operating layer for transport, freight, and school mobility</h4>
            <div className="space-y-3 mb-6">
              {[
                'Shared platform logic across tracking, billing, compliance, and operations',
                'Dedicated product experiences for each operating model',
                'Commercial flexibility from starter fleets to enterprise rollouts',
                'AI-powered route optimisation and predictive maintenance built-in',
                'Single dashboard for all fleet types — cabs, trucks, buses, and heavy vehicles',
              ].map((item, index) => (
                <div key={index} className="flex items-start gap-3">
                  <div className="w-2 h-2 rounded-full bg-primary-600 mt-2 flex-shrink-0" />
                  <span className="text-sm text-gray-700">{item}</span>
                </div>
              ))}
            </div>
            <button
              onClick={() => handleNavigation('/pricing')}
              className="inline-flex items-center gap-2 text-primary-600 font-semibold hover:text-primary-700 transition-colors"
            >
              See Pricing
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      );
    }

    if (activeMegaMenu === 'solutions') {
      return (
        <div className="grid lg:grid-cols-[1.25fr_0.75fr] gap-10">
          <div>
            <div className="mb-6">
              <p className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-2">
                Solutions
              </p>
              <h3 className="text-2xl font-bold text-navy-950">
                Choose the operating model closest to your business
              </h3>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              {solutions.map((solution) => (
                <button
                  key={solution.name}
                  onClick={() => handleNavigation(solution.href)}
                  className="text-left p-4 rounded-2xl border border-gray-100 bg-white hover:border-primary-200 hover:shadow-lg transition-all"
                >
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-xl bg-primary-50 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <solution.icon className="w-5 h-5 text-primary-600" />
                    </div>
                    <div>
                      <h4 className="text-base font-bold text-navy-950 mb-1">{solution.name}</h4>
                      <p className="text-sm text-gray-500 leading-relaxed">{solution.description}</p>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>

          <div className="space-y-4">
            <div className="bg-navy-950 rounded-3xl p-6 text-white">
              <p className="text-sm font-semibold text-primary-300 uppercase tracking-wide mb-2">
                Operational Focus
              </p>
              <h4 className="text-2xl font-bold mb-4 text-white">
                Built around how transport teams actually work
              </h4>
              <div className="space-y-3">
                {[
                  "Dispatch and trip execution",
                  "Billing and commercial control",
                  "Compliance and document readiness",
                  "Driver, vendor, and operator workflows",
                ].map((item, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <div className="w-2 h-2 rounded-full bg-accent-orange mt-2 flex-shrink-0" />
                    <span className="text-sm text-gray-300">{item}</span>
                  </div>
                ))}
              </div>
            </div>

            <button
              onClick={() => handleNavigation("/contact")}
              className="w-full text-left p-5 rounded-2xl border border-gray-100 bg-gray-50 hover:bg-gray-100 transition-colors"
            >
              <p className="text-sm font-semibold text-primary-600 mb-2">
                Need help deciding?
              </p>
              <h4 className="text-lg font-bold text-navy-950 mb-2">
                Talk to the team about your use case
              </h4>
              <p className="text-sm text-gray-600">
                We can help match your fleet size and workflows to the right
                product path.
              </p>
            </button>
          </div>
        </div>
      );
    }

    return (
      <div className="grid lg:grid-cols-[0.95fr_1.05fr] gap-10">
        <div>
          <div className="mb-6">
            <p className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-2">Company</p>
            <h3 className="text-2xl font-bold text-navy-950">Get to know the business behind the platform</h3>
          </div>

          <div className="grid grid-cols-2 gap-4">
            {companyLinks.map((link) => (
              <button
                key={link.name}
                onClick={() => handleNavigation(link.href)}
                className="text-left p-4 rounded-2xl border border-gray-100 bg-white hover:border-primary-200 hover:shadow-lg transition-all"
              >
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-xl bg-primary-50 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <link.icon className="w-5 h-5 text-primary-600" />
                  </div>
                  <div>
                    <h4 className="text-base font-bold text-navy-950 mb-1">{link.name}</h4>
                    <p className="text-sm text-gray-500 leading-relaxed">{link.description}</p>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          {[
            {
              title: 'Built in India',
              desc: 'Product and workflows shaped for Indian transport realities, compliance, and operating complexity.',
            },
            {
              title: 'Three Product Lines',
              desc: 'Employee transport, freight operations, and school mobility on one commercial platform.',
            },
            {
              title: 'Commercially Ready',
              desc: 'Transparent pricing, modular rollout logic, and enterprise expansion paths are already in place.',
            },
            {
              title: 'Talk to Sales',
              desc: 'Book a walkthrough, ask for a pricing fit, or explore a product line with the team.',
            },
          ].map((item) => (
            <div key={item.title} className="rounded-3xl border border-gray-100 bg-gray-50 p-6">
              <h4 className="text-lg font-bold text-navy-950 mb-2">{item.title}</h4>
              <p className="text-sm text-gray-600 leading-relaxed">{item.desc}</p>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <header
      onMouseLeave={() => setActiveMegaMenu(null)}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-white/95 backdrop-blur-md shadow-nav' : 'bg-white'}`}
    >
      <div className="container-main">
        <div className="flex items-center justify-between h-20">
          <Link to="/" className="flex items-center gap-2">
            <Logo size="lg" />
          </Link>

          <nav className="hidden lg:flex items-center gap-2">
            <button
              onMouseEnter={() => setActiveMegaMenu('products')}
              onClick={() => setActiveMegaMenu(activeMegaMenu === 'products' ? null : 'products')}
              className={menuButtonClass(isProductsActive || activeMegaMenu === 'products')}
            >
              Products
              <ChevronDown className={`w-4 h-4 transition-transform ${activeMegaMenu === 'products' ? 'rotate-180' : ''}`} />
            </button>

            <button
              onMouseEnter={() => setActiveMegaMenu('solutions')}
              onClick={() => setActiveMegaMenu(activeMegaMenu === 'solutions' ? null : 'solutions')}
              className={menuButtonClass(activeMegaMenu === 'solutions')}
            >
              Solutions
              <ChevronDown className={`w-4 h-4 transition-transform ${activeMegaMenu === 'solutions' ? 'rotate-180' : ''}`} />
            </button>

            <button
              onMouseEnter={() => setActiveMegaMenu('company')}
              onClick={() => setActiveMegaMenu(activeMegaMenu === 'company' ? null : 'company')}
              className={menuButtonClass(isCompanyActive || activeMegaMenu === 'company')}
            >
              Company
              <ChevronDown className={`w-4 h-4 transition-transform ${activeMegaMenu === 'company' ? 'rotate-180' : ''}`} />
            </button>

            {directLinks.map((link) => (
              <Link
                key={link.name}
                to={link.href}
                className={`px-3 py-2 rounded-xl font-medium transition-colors ${
                  location.pathname === link.href
                    ? 'bg-primary-50 text-primary-600'
                    : 'text-gray-700 hover:bg-gray-50 hover:text-primary-600'
                }`}
              >
                {link.name}
              </Link>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-4">
            <a href="tel:+919916500454" className="flex items-center gap-2 text-gray-600 hover:text-primary-600 transition-colors">
              <Phone className="w-4 h-4" />
              <span className="text-sm font-medium">+91 99165 00454</span>
            </a>
            <Link to="/contact" className="btn-primary text-sm">
              Request Demo
            </Link>
          </div>

          <button className="lg:hidden p-2 text-gray-700" onClick={() => setIsMenuOpen(!isMenuOpen)}>
            {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {activeMegaMenu && (
        <div className="hidden lg:block border-t border-gray-100 bg-white/95 backdrop-blur-md shadow-xl animate-fade-in">
          <div className="container-main py-8">{renderMegaMenu()}</div>
        </div>
      )}

      {isMenuOpen && (
        <div className="lg:hidden border-t border-gray-100 bg-white animate-fade-in max-h-[calc(100vh-5rem)] overflow-y-auto overscroll-contain">
          <div className="container-main py-4 pb-8 space-y-5">
            <div className="space-y-2">
              <p className="text-sm font-semibold text-gray-500 uppercase tracking-wide">Products</p>
              {products.map((product) => (
                <button
                  key={product.name}
                  onClick={() => handleNavigation(product.href)}
                  className="flex items-start gap-3 px-4 py-3 rounded-xl hover:bg-gray-50 w-full text-left"
                >
                  <product.icon className="w-5 h-5 text-primary-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <div className="text-gray-700 font-medium">{product.name}</div>
                    <div className="text-sm text-gray-500">{product.description}</div>
                  </div>
                </button>
              ))}
            </div>

            <div className="border-t border-gray-100 pt-4 space-y-2">
              <p className="text-sm font-semibold text-gray-500 uppercase tracking-wide">Solutions</p>
              {solutions.map((solution) => (
                <button
                  key={solution.name}
                  onClick={() => handleNavigation(solution.href)}
                  className="flex items-start gap-3 px-4 py-3 rounded-xl hover:bg-gray-50 w-full text-left"
                >
                  <solution.icon className="w-5 h-5 text-primary-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <div className="text-gray-700 font-medium">{solution.name}</div>
                    <div className="text-sm text-gray-500">{solution.description}</div>
                  </div>
                </button>
              ))}
            </div>

            <div className="border-t border-gray-100 pt-4 space-y-2">
              <p className="text-sm font-semibold text-gray-500 uppercase tracking-wide">Company</p>
              {companyLinks.map((link) => (
                <button
                  key={link.name}
                  onClick={() => handleNavigation(link.href)}
                  className="flex items-start gap-3 px-4 py-3 rounded-xl hover:bg-gray-50 w-full text-left"
                >
                  <link.icon className="w-5 h-5 text-primary-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <div className={`font-medium ${location.pathname === link.href ? 'text-primary-600' : 'text-gray-700'}`}>{link.name}</div>
                    <div className="text-sm text-gray-500">{link.description}</div>
                  </div>
                </button>
              ))}
            </div>

            <div className="border-t border-gray-100 pt-4 space-y-2">
              {directLinks.map((link) => (
                <Link
                  key={link.name}
                  to={link.href}
                  className={`block px-4 py-3 font-medium hover:bg-gray-50 rounded-xl ${
                    location.pathname === link.href ? 'text-primary-600' : 'text-gray-700'
                  }`}
                >
                  {link.name}
                </Link>
              ))}
            </div>

            <div className="border-t border-gray-100 pt-4 space-y-3">
              <a href="tel:+919916500454" className="flex items-center gap-2 px-4 py-3 text-gray-600">
                <Phone className="w-5 h-5" />
                <span className="font-medium">+91 99165 00454</span>
              </a>
              <Link to="/contact" className="btn-primary w-full text-center block">
                Request Demo
              </Link>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
